import React, { useCallback, useEffect, useRef } from 'react';
import { IconButtonWithTooltip, TextInput, useTranslate } from 'react-admin';
import { useForm, useFormState } from 'react-final-form';
import { makeStyles } from '@material-ui/core';
import { Autorenew } from '@material-ui/icons';
import { useSelector } from 'react-redux';

import { isEqual } from '@hisports/common/src/lodash';
import { SURFACE_EVENT_TYPES } from '@hisports/common/src/constants';

const useStyles = makeStyles(theme => ({
  generateButton: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
}));

const getNamesByIds = (ids, record) => {
  if (!ids || !record) return [];
  return ids
    .map((id) => record[id]?.name)
    .filter(Boolean);
};

const AutoGeneratedNameInput = (props) => {
  const translate = useTranslate();
  const { values } = useFormState();
  const { change } = useForm();
  const classes = useStyles();

  const { categories, leagues } = useSelector(state => ({
    categories: state.admin.resources.categories.data,
    leagues: state.admin.resources.offices.data,
  }));

  const { eventType, categoryIds, types, leagueIds } = values;

  const prevInputsRef = useRef(null);
  const userModifiedRef = useRef(false);

  const handleNameChange = (e) => {
    userModifiedRef.current = true;
    change('name', e.target.value);
  };

  const generateName = useCallback(() => {
    if (eventType === SURFACE_EVENT_TYPES.PRACTICE) {
      const type = translate('resources.practices.name', 1)
      change('name', type);
      return;
    };

    const leagueNames = getNamesByIds(leagueIds, leagues);
    const categoryNames = getNamesByIds(categoryIds, categories);

    const type = translate('resources.arenaslots.name', 1);
    const nameParts = [
      ...(types || []),
      ...leagueNames,
      ...categoryNames,
    ];

    const newName = `${type}${nameParts?.length > 0 ? ' - ' : ''}${nameParts.join('/')}`;

    change('name', newName);
    userModifiedRef.current = false;
  }, [eventType, categoryIds, types, leagueIds, categories, leagues, change, translate]);

  useEffect(() => {
    const currentInputs = { eventType, categoryIds, types, leagueIds };

    if (
      prevInputsRef.current &&
      !isEqual(prevInputsRef.current, currentInputs) &&
      !userModifiedRef.current
    ) {
      generateName();
    }

    prevInputsRef.current = currentInputs;
  }, [eventType, categoryIds, types, leagueIds, generateName]);

  const showGenerateButton = eventType !== SURFACE_EVENT_TYPES.PRACTICE && userModifiedRef.current;
  const showGeneratedHelperText = eventType !== SURFACE_EVENT_TYPES.PRACTICE && !userModifiedRef.current;

  return (
    <div className={classes.generateButton}>
      <TextInput
        onChange={handleNameChange}
        helperText={showGeneratedHelperText ? 'resources.arenaslots.helpers.auto_generated' : false}
        {...props}
      />
      {showGenerateButton && <IconButtonWithTooltip color="primary" label="resources.arenaslots.tooltips.generate_name" onClick={generateName}>
        <Autorenew />
      </IconButtonWithTooltip>}
    </div>
  );
};

export default AutoGeneratedNameInput;
